/**
 * White-label brand derivation for LabBOXX.
 *
 * A tenant gets exactly ONE lever: a single brand hex. From it this module
 * derives the brand ramp AND the whole brand-tinted structural estate, in
 * both modes.
 *
 * WHAT IS FROZEN AND WHY. The four clinical roles — success / warning /
 * destructive / info — are not derived and are not reachable from here. The
 * red that means "critical" on a haematology panel has to be the same red on
 * the printed report and in the analyzer alert list, so a tenant is not
 * allowed anywhere near a result flag. The ink tiers are frozen for the same
 * kind of reason: they were sized for the darkest point the canvas wash can
 * reach, and a tenant cannot make text unreadable by picking a colour.
 *
 * WHAT MOVES, AND WHY IT IS MORE THAN THE BUTTON. index.css used to freeze
 * the structural neutrals on a blue-tinted grey, which is invisible while the
 * brand IS blue and is exactly the "colours are clashing" complaint the moment
 * a lab picks Burgundy or Violet: every screen keeps a faint blue cast under a
 * purple brand, and the page header stayed sky-blue on top of it. The cure is
 * not to desaturate the app to sterile grey — it is to re-hue the same greys
 * to the tenant's own hue. Each derived neutral keeps the LIGHTNESS AND
 * SATURATION GEOMETRY of the token it replaces and swaps only the hue, so
 * every contrast figure measured for the default palette carries over — and
 * the gate re-measures against the derived grounds anyway.
 *
 * THREE THINGS MAKE THIS LabBOXX-SPECIFIC rather than a copy of PetsBOXX's:
 *
 * 1. **The tokens are HSL TRIPLETS, not hex** — index.css declares
 *    `--primary: 210 100% 40%` and Tailwind wraps every use in `hsl(...)`.
 *    Writing a hex into those variables produces `hsl(#1D4ED8)`, which is
 *    invalid, and the colour silently falls back to nothing. It is also what
 *    makes the re-hue cheap: swapping a hue IS swapping the first number.
 *
 * 2. **LabBOXX has dark mode, and the guard is therefore MODE-AWARE.** That is
 *    not a detail: a too-dark brand must be LIGHTENED on a dark ground, not
 *    darkened. Darkening it there leaves the fill invisible against the
 *    background while the label on top stays perfectly legible — the button
 *    loses its shape without looking broken.
 *
 * 3. ⭐ **The override is a trailing <style>, not inline properties on
 *    <html>, and that changes what has to be emitted.** index.css declares its
 *    tokens inside `@layer base`; an UNLAYERED rule beats a layered one
 *    whatever the specificity, so the injected `:root{…}` block outranks
 *    index.css's `.dark{…}`. Consequence: every token emitted in the `:root`
 *    block MUST also be emitted in the `:root.dark` block, or its light value
 *    leaks onto a dark screen — a failure only dark-mode users ever see.
 *    `emit()` below builds both blocks from the SAME key list so they cannot
 *    drift, and check_brand_contrast.cjs asserts the two key sets are equal.
 *
 *    (This also retires the old MutationObserver on <html>. Inline properties
 *    outrank every selector, so the previous implementation had to re-derive
 *    the brand by hand on every mode flip; a stylesheet carrying both blocks
 *    lets the cascade do it.)
 *
 * No dependencies — this runs before anything else paints.
 */

/** The design default: the blue already declared in index.css (210 100% 40%). */
export const DESIGN_DEFAULT = '#0066CC'

/* Mirrors the frozen structural values in index.css. If those change, these
   change with them; they are the surfaces the guard measures against. */
const SURFACE = '#FFFFFF'       // --card, light mode
const INK_DARK = '#101419'      // --foreground, light mode
const INK_LIGHT = '#FFFFFF'

/* The frozen LABEL tier, mirrored from index.css's `--faint` in both modes.
   It is the lowest ink tier that still owes AA, so it is the first thing the
   canvas wash breaks — the blobs below are walked until it survives them.
   If index.css moves, these move with it; check_brand_contrast.cjs reads the
   real stylesheet, so a drift between the two shows up as a gate failure
   rather than as an unreadable label on someone's screen. */
const FAINT_LIGHT = '#4F5963'   // --faint, light  (210 11% 35%)
const FAINT_DARK = '#828C97'    // --faint, dark   (210 9% 55%)

/* The alphas the three canvas blobs are painted at (.app-canvas). They are
   here because the blob derivation has to know them: the duty is a property
   of the COMPOSITE, not of the blob colour. If index.css changes an alpha,
   change it here too or the walk guarantees the wrong thing. */
const WASH_LIGHT = [0.12, 0.12, 0.10]
const WASH_DARK = [0.26, 0.20, 0.16]

const AA_TEXT = 4.5
const NON_TEXT = 3

/**
 * ⭐ The walks aim slightly ABOVE the floor, and that is not a fudge.
 *
 * A derivation measures a colour and then EMITS A ROUNDED ONE — one decimal of
 * h/s/l, then an integer RGB when the browser resolves it, then another
 * rounding wherever a composite is computed. Each step moves the ratio by a
 * hundredth or two. Walk to exactly 4.50 and the shipped value lands at 4.49,
 * which is a fail the moment anything measures the artifact rather than the
 * intermediate — four presets did exactly that.
 *
 * So the derivation clears the floor with enough room to survive its own
 * quantisation, and the gate keeps measuring the TRUE floor. Widening the
 * margin here is legitimate; lowering the gate's floor is not.
 */
const QUANTISATION_MARGIN = 0.05
const AA_WALK = AA_TEXT + QUANTISATION_MARGIN
const NON_TEXT_WALK = NON_TEXT + QUANTISATION_MARGIN

interface RGB { r: number; g: number; b: number }
interface HSL { h: number; s: number; l: number }

/** Accepts #rgb, #rrggbb, with or without the hash. null if unparseable. */
export function parseHex(input: string): RGB | null {
  if (typeof input !== 'string') return null
  let h = input.trim().replace(/^#/, '')
  if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2]
  if (!/^[0-9a-fA-F]{6}$/.test(h)) return null
  return {
    r: parseInt(h.slice(0, 2), 16),
    g: parseInt(h.slice(2, 4), 16),
    b: parseInt(h.slice(4, 6), 16),
  }
}

/** Normalise any accepted hex form to #RRGGBB uppercase; null if unparseable. */
export function normaliseHex(input: string): string | null {
  const rgb = parseHex(input)
  return rgb ? rgbToHex(rgb) : null
}

export function rgbToHsl({ r, g, b }: RGB): HSL {
  const rn = r / 255, gn = g / 255, bn = b / 255
  const max = Math.max(rn, gn, bn)
  const min = Math.min(rn, gn, bn)
  const l = (max + min) / 2
  if (max === min) return { h: 0, s: 0, l: l * 100 }
  const d = max - min
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
  let h: number
  if (max === rn) h = ((gn - bn) / d + (gn < bn ? 6 : 0))
  else if (max === gn) h = (bn - rn) / d + 2
  else h = (rn - gn) / d + 4
  return { h: h * 60, s: s * 100, l: l * 100 }
}

function hslToRgb({ h, s, l }: HSL): RGB {
  const sn = Math.max(0, Math.min(100, s)) / 100
  const ln = Math.max(0, Math.min(100, l)) / 100
  const c = (1 - Math.abs(2 * ln - 1)) * sn
  const hp = ((h % 360) + 360) % 360 / 60
  const x = c * (1 - Math.abs((hp % 2) - 1))
  let r = 0, g = 0, b = 0
  if (hp < 1) [r, g, b] = [c, x, 0]
  else if (hp < 2) [r, g, b] = [x, c, 0]
  else if (hp < 3) [r, g, b] = [0, c, x]
  else if (hp < 4) [r, g, b] = [0, x, c]
  else if (hp < 5) [r, g, b] = [x, 0, c]
  else [r, g, b] = [c, 0, x]
  const m = ln - c / 2
  return {
    r: Math.round((r + m) * 255),
    g: Math.round((g + m) * 255),
    b: Math.round((b + m) * 255),
  }
}

function rgbToHex({ r, g, b }: RGB): string {
  const c = (n: number) =>
    Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0')
  return `#${c(r)}${c(g)}${c(b)}`.toUpperCase()
}

function channelLuminance(v: number): number {
  const c = v / 255
  return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
}

/** WCAG 2.1 relative luminance, 0..1. */
export function luminance(c: RGB): number {
  return 0.2126 * channelLuminance(c.r)
    + 0.7152 * channelLuminance(c.g)
    + 0.0722 * channelLuminance(c.b)
}

/** WCAG 2.1 contrast ratio between two hex colours, 1..21. Order-independent. */
export function contrastRatio(a: string, b: string): number {
  const ca = parseHex(a)
  const cb = parseHex(b)
  if (!ca || !cb) return 1
  const la = luminance(ca)
  const lb = luminance(cb)
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05)
}

const round = (n: number) => Math.round(n * 10) / 10

/**
 * ⭐ Quantise to the precision the token will actually be WRITTEN at.
 *
 * Every triplet ships rounded to one decimal. Without this, a guard walks an
 * unrounded colour until it clears 4.5:1, emits the rounded one, and ships
 * 4.48 — the walk measured a colour that never existed. It is a hundredth of
 * a ratio and it cost two presets (Forest and Burgundy, dark mode) the moment
 * the gate measured the emitted triplets instead of the internal maths.
 * Measure the artifact you ship.
 */
function quantise({ h, s, l }: HSL): HSL {
  return { h: round(((h % 360) + 360) % 360), s: round(s), l: round(l) }
}

/** `H S% L%` — the triplet form the CSS variables require. */
function triplet(hsl: HSL): string {
  const { h, s, l } = quantise(hsl)
  return `${h} ${s}% ${l}%`
}

/** A triplet back to hex, so the gate (and any preview) can measure it. */
export function tripletToHex(t: string): string {
  const m = /^\s*(-?[\d.]+)\s+(-?[\d.]+)%\s+(-?[\d.]+)%\s*$/.exec(t)
  if (!m) return '#000000'
  return rgbToHex(hslToRgb({ h: +m[1], s: +m[2], l: +m[3] }))
}

/**
 * Walk lightness in `step` increments until `test` passes.
 *
 * Returns the first passing colour, or the end-stop if none does — the
 * end-stop is black or white, which passes against anything the guard cares
 * about, so this cannot return an illegible value.
 */
function walkLightness(hsl: HSL, step: number, test: (hex: string) => boolean): HSL {
  let l = hsl.l
  for (let i = 0; i < 300; i++) {
    // Quantised: the test must see the colour that will be emitted, not the
    // full-precision intermediate. See quantise().
    const candidate = quantise({ ...hsl, l })
    if (test(rgbToHex(hslToRgb(candidate)))) return candidate
    l += step
    if (l <= 0 || l >= 100) break
  }
  return quantise({ ...hsl, l: step < 0 ? 0 : 100 })
}

/** The hex a given HSL will REALLY paint once emitted — quantised first, so
 *  every guard measures the shipped value rather than an intermediate. */
const hex = (h: HSL) => rgbToHex(hslToRgb(quantise(h)))

/** src-over composite of `fg` at `alpha` on an opaque `bg`. */
function over(fg: string, bg: string, alpha: number): string {
  const a = parseHex(fg)!
  const b = parseHex(bg)!
  return rgbToHex({
    r: a.r * alpha + b.r * (1 - alpha),
    g: a.g * alpha + b.g * (1 - alpha),
    b: a.b * alpha + b.b * (1 - alpha),
  })
}

/**
 * A canvas wash blob, walked until the frozen LABEL tier survives it.
 *
 * ⭐ THE BLOB'S DUTY IS A PROPERTY OF THE COMPOSITE, NOT OF THE BLOB. A wash
 * cannot be judged by its own contrast against anything — what matters is the
 * ground it produces once painted at `alpha`, and whether the palest ink the
 * design still owes AA to can be read on THAT.
 *
 * Pinning a lightness and hoping does not survive the ends of the range. A
 * white brand in dark mode lifts the near-black ground into a light patch; a
 * saturated yellow does it at any HSL lightness you like, because HSL
 * lightness is not luminance and yellow carries far more of it than blue at
 * the same number. Both were found by the gate, not by inspection.
 *
 * So the blob walks AWAY FROM THE INK — lighter under dark ink, darker under
 * light ink — until the label clears 4.5:1 on the composite. Typical brands
 * never move (all eight presets keep their designed lightness); only the
 * extremes get pulled in, and there the canvas quietly fades rather than
 * making a screen unreadable. Legibility outranks decoration.
 */
function washBlob(seed: HSL, ground: string, alpha: number, ink: string, dark: boolean): HSL {
  const ok = (c: string) => contrastRatio(ink, over(c, ground, alpha)) >= AA_WALK
  return walkLightness(seed, dark ? -0.5 : +0.5, ok)
}

/* ── the ramp (kept for the settings previews) ─────────────────────────── */

export interface BrandRamp {
  /** `H S% L%` — the triplet form the CSS variables require. */
  primaryTriplet: string
  /** `H S% L%` for the ink ON the fill. */
  foregroundTriplet: string
  /** The guarded fill as a hex, for previews and swatches. */
  primaryHex: string
  /** True when the guard had to move the tenant's colour. */
  adjusted: boolean
}

/**
 * The brand fill for one mode, under its legibility guard.
 *
 * `primary` is not only a button fill — `text-primary` is a link and
 * `border-primary` is a selected edge — so it owes AA against both the ground
 * it will sit on and a card. Brand fidelity is preserved wherever it legally
 * can be: a hex that already clears both is used COMPLETELY UNTOUCHED, and a
 * brand that does not clear is moved only as far as the guard requires, and
 * only in the direction that mode allows (see the header).
 */
export function deriveBrandRamp(input: string, dark: boolean): BrandRamp {
  const rgb = parseHex(input) || parseHex(DESIGN_DEFAULT)!
  const hsl = rgbToHsl(rgb)
  const t = tintsFor(hsl)
  const ground = dark ? t.dGroundHex : t.groundHex
  const surface = dark ? t.dCardHex : SURFACE

  const clears = (c: string) =>
    contrastRatio(c, ground) >= AA_WALK && contrastRatio(c, surface) >= AA_WALK

  const primary = clears(hex(hsl))
    ? hsl
    : walkLightness(hsl, dark ? +0.4 : -0.4, clears)

  const primaryHex = hex(primary)
  // The ink is chosen, not assumed: a yellow brand needs black on it and a
  // navy one needs white, and picking wrong makes the label vanish.
  const fg = contrastRatio(primaryHex, INK_LIGHT) >= contrastRatio(primaryHex, INK_DARK)
    ? INK_LIGHT
    : INK_DARK

  return {
    primaryTriplet: triplet(primary),
    foregroundTriplet: triplet(rgbToHsl(parseHex(fg)!)),
    primaryHex,
    adjusted: Math.abs(primary.l - hsl.l) > 0.5,
  }
}

/* ── the tinted structural estate ──────────────────────────────────────── */

interface Tints {
  groundHex: string
  dGroundHex: string
  dCardHex: string
  mk: (s: number, l: number, hueShift?: number) => HSL
  canvasShift: (shift: number) => number
}

/**
 * The geometry of every derived neutral, in one place.
 *
 * `f` fades the whole tint out as the brand loses saturation. Without it a
 * grey brand would inherit hue 0 — an achromatic colour still carries one —
 * and the entire app would blush pink. A Graphite lab gets a cool slate wash;
 * a true-grey brand gets a true-grey app.
 */
function tintsFor(hsl: HSL): Tints {
  const f = Math.max(0, Math.min(1, hsl.s / 25))
  const mk = (s: number, l: number, hueShift = 0): HSL =>
    ({ h: hsl.h + hueShift, s: s * f, l })

  /* Canvas companions walk ±30° from the brand — but that walk can land in
     the MUD BAND (0°–120°: red-orange through olive), where a mid-saturation
     wash at canvas alpha reads as beige smeared across every page ground.
     Under Hot Pink (~335°) the +30° companion comes out terracotta. The rule
     is the owner's: earthy hues only when the BRAND is earthy. So a brand
     inside the band keeps its warm companions — that tenant chose them
     (Copper, Forest) — and any other brand's companion that lands in the band
     reflects to the cool side, doubled so it clears the band with room. */
  const inMud = (h: number) => (((h % 360) + 360) % 360) <= 120
  const canvasShift = (shift: number) => {
    if (inMud(hsl.h)) return shift
    if (!inMud(hsl.h + shift)) return shift
    return -2 * shift
  }

  return {
    groundHex: hex(mk(16, 96.9)),
    dGroundHex: hex(mk(30, 6.1)),
    dCardHex: hex(mk(25, 10.4)),
    mk,
    canvasShift,
  }
}

/** Token name → `H S% L%`. */
export type TokenMap = Record<string, string>

export interface BrandTheme {
  light: TokenMap
  dark: TokenMap
}

/**
 * The whole theme from one tenant hex — the brand ramp AND the brand-tinted
 * structural estate, for both modes.
 *
 * Null when the hex is unparseable: the caller drops the override and
 * index.css supplies the design defaults. Painting something arbitrary
 * instead would hide a bad value in the tenant record forever.
 */
export function deriveBrandTheme(input: string): BrandTheme | null {
  const rgb = parseHex(input)
  if (!rgb) return null

  const hsl = rgbToHsl(rgb)
  const { mk, canvasShift } = tintsFor(hsl)

  /* ── structural, light ─────────────────────────────────────────────── */
  const ground = mk(16, 96.9)
  const groundHex = hex(ground)

  /* An interactive field edge is a component boundary and owes 3:1 (WCAG
     1.4.11) against the ground AND a card. At a FIXED lightness that duty
     fails by a hair for yellow-green hues, so the edge is WALKED to its duty
     like the ink tiers are, rather than pinned and hoped for. */
  const inputEdge = walkLightness(mk(8, 54.1), -0.5, (c) =>
    contrastRatio(c, groundHex) >= NON_TEXT_WALK && contrastRatio(c, SURFACE) >= NON_TEXT_WALK)

  /* ── structural, dark ──────────────────────────────────────────────── */
  const dGround = mk(30, 6.1)
  const dCard = mk(25, 10.4)
  const dGroundHex = hex(dGround)
  const dCardHex = hex(dCard)
  const dInputEdge = walkLightness(mk(12, 43.4), +0.5, (c) =>
    contrastRatio(c, dGroundHex) >= NON_TEXT_WALK && contrastRatio(c, dCardHex) >= NON_TEXT_WALK)

  /* ── the brand fill, per mode ──────────────────────────────────────── */
  const lightFill = guardedFill(hsl, groundHex, SURFACE, false)
  const darkFill = guardedFill(hsl, dGroundHex, dCardHex, true)

  /* ── the brand tint and the ink on it ──────────────────────────────────
     The tint's saturation is pulled back so a hot-pink lab gets a WASH, not
     a second brand colour competing with the clinical roles for attention. */
  const tint = { h: hsl.h, s: Math.min(hsl.s, 35), l: 93 }
  const tintHex = hex(tint)
  const dTint = { h: hsl.h, s: Math.min(hsl.s, 60), l: 20 }
  const dTintHex = hex(dTint)

  /* primary-700 carries text on the tint, on the ground and on a card, and it
     is also the hover fill.

     ⭐ The walk is SEEDED one visible step away rather than starting on the
     fill. Without the seed a brand that already satisfies every guard returns
     ITSELF, and hover becomes a no-op — the button would not react to the
     pointer at all. That is not a contrast bug and no contrast check finds it;
     it reads as a dead control. Darker is the right direction on a light
     ground, but a brand already on the black floor has nowhere darker to go
     and would clamp back to the fill, reinstating exactly the no-op the seed
     exists to prevent — those walk lighter instead. */
  const strongEnough = (c: string) =>
    contrastRatio(c, tintHex) >= AA_WALK &&
    contrastRatio(c, groundHex) >= AA_WALK &&
    contrastRatio(c, SURFACE) >= AA_WALK
  const darker = walkLightness({ ...lightFill, l: lightFill.l - 8 }, -0.5, strongEnough)
  const ink700 = hex(darker) !== hex(lightFill)
    ? darker
    : walkLightness({ ...lightFill, l: lightFill.l + 8 }, +0.5, strongEnough)

  /* In dark mode the ink on the deep tint has to be LIGHT, so the same duty
     is walked in the other direction from a light seed. */
  const dStrongEnough = (c: string) =>
    contrastRatio(c, dTintHex) >= AA_WALK &&
    contrastRatio(c, dGroundHex) >= AA_WALK &&
    contrastRatio(c, dCardHex) >= AA_WALK
  const dInk700 = walkLightness({ h: hsl.h, s: Math.min(hsl.s, 90), l: 75 }, +0.5, dStrongEnough)

  /* The focus ring must be discernible against the ground as a non-text cue
     (WCAG 1.4.11). Reuse the fill if it clears, else fall to the already
     guarded ink — never leave it on a colour nobody can find. */
  const ring = contrastRatio(hex(lightFill), groundHex) >= NON_TEXT ? lightFill : ink700
  const dRing = contrastRatio(hex(darkFill), dGroundHex) >= NON_TEXT ? darkFill : dInk700

  /* ── the canvas ────────────────────────────────────────────────────────
     Blob 1 is the brand's own hue, but it is NOT `--primary`: the fill is
     guarded for legibility ON a ground, which is a different duty from being
     painted INTO one at 12%, and reusing it makes a white-brand tenant wash
     its whole dark app into a light grey patch. It gets its own token and its
     own walk, like the other two. */
  const canvas1 = washBlob(mk(50, 52), groundHex, WASH_LIGHT[0], FAINT_LIGHT, false)
  const canvas2 = washBlob(mk(54, 52, canvasShift(+30)), groundHex, WASH_LIGHT[1], FAINT_LIGHT, false)
  const canvas3 = washBlob(mk(60, 45, canvasShift(-30)), groundHex, WASH_LIGHT[2], FAINT_LIGHT, false)
  const dCanvas1 = washBlob(mk(50, 74), dGroundHex, WASH_DARK[0], FAINT_DARK, true)
  const dCanvas2 = washBlob(mk(53, 74, canvasShift(+30)), dGroundHex, WASH_DARK[1], FAINT_DARK, true)
  const dCanvas3 = washBlob(mk(44, 67, canvasShift(-30)), dGroundHex, WASH_DARK[2], FAINT_DARK, true)

  /* ⭐ ONE key list, two blocks. The dark block must redefine every token the
     light block defines — see note 3 in the header — so both are built from
     the same object shape rather than written out twice. */
  return {
    light: {
      '--background': triplet(ground),
      '--muted': triplet(mk(18, 94)),
      '--secondary': triplet(mk(18, 94)),
      '--border': triplet(mk(17, 89.8)),
      '--hairline': triplet(mk(18, 94)),
      '--input': triplet(inputEdge),
      '--primary': triplet(lightFill),
      '--primary-foreground': triplet(inkFor(hex(lightFill))),
      '--primary-100': triplet(tint),
      '--primary-700': triplet(ink700),
      '--accent': triplet(tint),
      '--accent-foreground': triplet(ink700),
      '--ring': triplet(ring),
      '--canvas-1': triplet(canvas1),
      '--canvas-2': triplet(canvas2),
      '--canvas-3': triplet(canvas3),
      '--sidebar-background': triplet(mk(17, 94.9)),
      '--sidebar-border': triplet(mk(17, 89.8)),
      '--sidebar-primary': triplet(lightFill),
      '--sidebar-primary-foreground': triplet(inkFor(hex(lightFill))),
      '--sidebar-accent': triplet(tint),
      '--sidebar-accent-foreground': triplet(ink700),
      '--sidebar-ring': triplet(ring),
    },
    dark: {
      '--background': triplet(dGround),
      '--muted': triplet(mk(22, 14.1)),
      '--secondary': triplet(mk(22, 14.1)),
      '--border': triplet(mk(20, 19.2)),
      '--hairline': triplet(mk(21, 15.5)),
      '--input': triplet(dInputEdge),
      '--primary': triplet(darkFill),
      '--primary-foreground': triplet(inkFor(hex(darkFill))),
      '--primary-100': triplet(dTint),
      '--primary-700': triplet(dInk700),
      '--accent': triplet(dTint),
      '--accent-foreground': triplet(dInk700),
      '--ring': triplet(dRing),
      '--canvas-1': triplet(dCanvas1),
      '--canvas-2': triplet(dCanvas2),
      '--canvas-3': triplet(dCanvas3),
      '--sidebar-background': triplet(mk(30, 8.4)),
      '--sidebar-border': triplet(mk(20, 16.7)),
      '--sidebar-primary': triplet(darkFill),
      '--sidebar-primary-foreground': triplet(inkFor(hex(darkFill))),
      '--sidebar-accent': triplet(dTint),
      '--sidebar-accent-foreground': triplet(dInk700),
      '--sidebar-ring': triplet(dRing),
      /* Dark-only, and not present in the light block because light mode has
         none of these to restate: the card and popover surfaces themselves
         are white there and never move. */
      '--card': triplet(dCard),
      '--popover': triplet(mk(25, 12.5)),
    },
  }
}

/** White or near-black on a fill, whichever clears. Settled by construction:
 *  contrast is symmetric, so a fill at 4.5:1 against white gives white ink
 *  4.5:1 back. */
function inkFor(fillHex: string): HSL {
  const light = contrastRatio(fillHex, INK_LIGHT)
  const dark = contrastRatio(fillHex, INK_DARK)
  return rgbToHsl(parseHex(light >= dark ? INK_LIGHT : INK_DARK)!)
}

function guardedFill(hsl: HSL, ground: string, surface: string, dark: boolean): HSL {
  const clears = (c: string) =>
    contrastRatio(c, ground) >= AA_WALK && contrastRatio(c, surface) >= AA_WALK
  return clears(hex(hsl)) ? hsl : walkLightness(hsl, dark ? +0.4 : -0.4, clears)
}

/* ── application ───────────────────────────────────────────────────────── */

const STYLE_ID = 'labboxx-brand'

function emit(theme: BrandTheme): string {
  const block = (sel: string, map: TokenMap) =>
    `${sel}{${Object.entries(map).map(([k, v]) => `${k}:${v}`).join(';')}}`
  /* `:root.dark` is (0,2,0) and sits after `:root` (0,1,0) in the same sheet,
     so it wins twice over. Both are unlayered and therefore beat index.css's
     own `@layer base` blocks regardless of order. */
  return `${block(':root', theme.light)}\n${block(':root.dark', theme.dark)}`
}

/** The colour currently in force, remembered so the settings screens can
 *  re-apply without another round trip. */
let currentBrand = ''

/**
 * Paint a brand hex onto the document.
 *
 * Written as a <style> element appended to <head> rather than inline custom
 * properties on documentElement. A trailing UNLAYERED stylesheet is exactly
 * as strong as this override needs to be; inline properties would instead
 * beat every selector unconditionally, which is what forced the old
 * implementation to re-derive the palette by hand on every dark-mode flip.
 *
 * An empty or unparseable value REMOVES the override so the design default in
 * index.css takes over again — that is what "reset to default" has to mean,
 * and it is why the element is removed rather than rewritten with a hardcoded
 * blue.
 */
export function applyBrandColor(hex: string | null | undefined) {
  if (typeof document === 'undefined') return
  currentBrand = (hex || '').trim()

  const existing = document.getElementById(STYLE_ID)
  const theme = currentBrand ? deriveBrandTheme(currentBrand) : null
  if (!theme) {
    existing?.remove()
    return
  }

  const css = emit(theme)
  if (existing) {
    if (existing.textContent !== css) existing.textContent = css
    // Keep it last in <head> so later-injected stylesheets cannot outrank it.
    if (existing !== document.head.lastElementChild) document.head.appendChild(existing)
    return
  }
  const el = document.createElement('style')
  el.id = STYLE_ID
  el.textContent = css
  document.head.appendChild(el)
}

/** The brand currently painted, or '' when the design default is in force. */
export function currentBrandColor(): string {
  return currentBrand
}

/** Cache key for the brand, so a reload paints the tenant's colour on the
 *  FIRST frame instead of flashing LabBOXX blue until the fetch lands. */
export const BRAND_CACHE_KEY = 'labboxx-brand-color'

/** Paint the last known brand immediately, before any network call. */
export function applyCachedBrand() {
  try {
    applyBrandColor(localStorage.getItem(BRAND_CACHE_KEY) || '')
  } catch {
    /* private-mode localStorage — the design default is a fine fallback */
  }
}

export function cacheBrand(hex: string) {
  try {
    localStorage.setItem(BRAND_CACHE_KEY, hex || '')
  } catch { /* ignore */ }
}
